/**
 * Onglet "Clients" du back-office B2B.
 *
 * Carnet d'adresses unifié : import auto depuis leads + ajout manuel.
 * Recherche full-text sur nom / contact / email / ville.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Plus, Search, Mail, Phone, MapPin, Edit3, Trash2, FileText } from 'lucide-react';
import api from '../../../api';
import { toast } from 'sonner';

const EMPTY = { name: '', contact_name: '', email: '', phone: '', city: '', address: '', siret: '', structure_type: '', notes: '' };

export const B2BClientsTab = ({ onCreateDocument }) => {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.b2bListClients(q);
      setClients(r.items || []);
    } catch (e) {
      toast.error('Erreur chargement clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      name: c.name || '', contact_name: c.contact_name || '', email: c.email || '',
      phone: c.phone || '', city: c.city || '', address: c.address || '',
      siret: c.siret || '', structure_type: c.structure_type || '', notes: c.notes || '',
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || form.name.trim().length < 2) {
      toast.error('Le nom du client est obligatoire');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      payload.name = form.name.trim();
      if (editId) {
        await api.b2bUpdateClient(editId, payload);
        toast.success('Client mis à jour');
      } else {
        await api.b2bCreateClient(payload);
        toast.success('Client créé');
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Supprimer définitivement le client "${c.name}" ?`)) return;
    try {
      await api.b2bDeleteClient(c.id);
      toast.success('Client supprimé');
      await load();
    } catch (e) {
      toast.error(e.message || 'Suppression refusée (documents liés ?)');
    }
  };

  return (
    <div className="space-y-4" data-testid="b2b-clients-list">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher par nom, contact, email, ville…"
            className="pl-9"
            data-testid="b2b-clients-search"
          />
        </div>
        <Button onClick={openCreate} data-testid="b2b-clients-create">
          <Plus size={16} className="mr-1.5" /> Nouveau client
        </Button>
      </div>

      {loading && clients.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Chargement…</div>
      ) : clients.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="text-muted-foreground">Aucun client dans le carnet d'adresses.</div>
            <Button className="mt-4" onClick={openCreate}>
              <Plus size={16} className="mr-1.5" />Ajouter mon premier client
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {clients.map((c) => (
            <Card key={c.id} data-testid={`b2b-client-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-black truncate">{c.name}</h3>
                      {c.structure_type && <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-muted">{c.structure_type}</span>}
                      {c.source === 'lead' && <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-600">Lead</span>}
                    </div>
                    {c.contact_name && <div className="text-xs mt-1">{c.contact_name}</div>}
                    <div className="mt-2 space-y-1 text-xs">
                      {c.email && <div className="flex items-center gap-1.5 min-w-0"><Mail size={11} className="text-muted-foreground flex-shrink-0" /><a href={`mailto:${c.email}`} className="text-[hsl(var(--primary))] hover:underline truncate">{c.email}</a></div>}
                      {c.phone && <div className="flex items-center gap-1.5"><Phone size={11} className="text-muted-foreground" /><a href={`tel:${c.phone}`} className="text-[hsl(var(--primary))] hover:underline">{c.phone}</a></div>}
                      {c.city && <div className="flex items-center gap-1.5"><MapPin size={11} className="text-muted-foreground" />{c.city}</div>}
                      {c.siret && <div className="text-muted-foreground">SIRET : {c.siret}</div>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`b2b-client-edit-${c.id}`} title="Modifier">
                      <Edit3 size={14} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(c)} data-testid={`b2b-client-delete-${c.id}`} title="Supprimer" className="text-rose-600 hover:text-rose-700">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="b2b-client-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? 'Modifier le client' : 'Nouveau client B2B'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label htmlFor="name">Nom de l'établissement *</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Auto-École de la Liberté" data-testid="b2b-client-form-name" />
            </div>
            <div>
              <Label htmlFor="contact_name">Nom du contact</Label>
              <Input id="contact_name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="M. Dupont, directeur" />
            </div>
            <div>
              <Label htmlFor="structure_type">Type</Label>
              <Input id="structure_type" value={form.structure_type} onChange={(e) => setForm({ ...form, structure_type: e.target.value })} placeholder="auto-école / moto-école / CFA" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="contact@auto-ecole.fr" data-testid="b2b-client-form-email" />
            </div>
            <div>
              <Label htmlFor="phone">Téléphone</Label>
              <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="04 74 00 00 00" />
            </div>
            <div>
              <Label htmlFor="city">Ville</Label>
              <Input id="city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Bourgoin-Jallieu" />
            </div>
            <div>
              <Label htmlFor="siret">SIRET</Label>
              <Input id="siret" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} placeholder="123 456 789 00012" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="address">Adresse complète</Label>
              <Textarea id="address" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="12 rue de la Liberté\n38300 Bourgoin-Jallieu" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="notes">Notes internes</Label>
              <Textarea id="notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Préfère être joint le matin, budget annuel ~5k€, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="b2b-client-form-save">
              {saving ? 'Sauvegarde…' : (editId ? 'Mettre à jour' : 'Créer le client')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
